'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { api } from './api';
import type { User } from '@/types';

/**
 * Decode the `exp` Unix timestamp from a JWT payload without importing a
 * library. Returns null if the token is malformed or missing the claim.
 */
function parseJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  setTokens: (accessToken: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  clearSession: () => void;
  refreshAuth: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEYS = {
  accessToken: 'wp_at',
  refreshToken: 'wp_rt',
  user: 'wp_user',
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isLoading: true,
  });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshUser = useCallback(async () => {
    const at = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (!at) return;
    try {
      api.setAccessToken(at);
      const freshUser = await api.getMe(true);
      if (freshUser) {
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(freshUser));
        setState((prev) => {
          if (JSON.stringify(prev.user) !== JSON.stringify(freshUser)) {
            return { ...prev, user: freshUser };
          }
          return prev;
        });
      }
    } catch {
      // Silent error handling for background profile sync
    }
  }, []);

  // Restore session from localStorage on mount & refresh profile
  useEffect(() => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
      const userJson = localStorage.getItem(STORAGE_KEYS.user);
      const user = userJson ? (JSON.parse(userJson) as User) : null;

      if (accessToken && refreshToken && user) {
        api.setAccessToken(accessToken);
        setState({ user, accessToken, refreshToken, isLoading: false });
        refreshUser();
      } else {
        setState((s) => ({ ...s, isLoading: false }));
      }
    } catch {
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, [refreshUser]);

  const setTokens = useCallback((accessToken: string, refreshToken: string, user: User) => {
    localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
    localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
    api.setAccessToken(accessToken);
    setState({ user, accessToken, refreshToken, isLoading: false });

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const bc = new BroadcastChannel('wp_auth_sync');
        bc.postMessage({ type: 'PROFILE_UPDATED' });
        bc.close();
      } catch {
        // ignore
      }
    }
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.user);
    api.setAccessToken(null);
    setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    // Clear tokens immediately before any async work so that any refresh callback
    // triggered by the logout API call finds no token and returns false, breaking
    // the recursive refresh → logout → refresh loop.
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.user);
    api.setAccessToken(null);
    setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
    if (refreshToken) {
      try {
        await api.logout(refreshToken);
      } catch {
        // ignore — already cleared locally
      }
    }
    router.push('/login');
  }, [router]);

  const forceLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.user);
    api.setAccessToken(null);
    setState({ user: null, accessToken: null, refreshToken: null, isLoading: false });
    router.push('/login');
  }, [router]);

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!refreshToken) return false;

    try {
      const tokens = await api.refresh(refreshToken);
      // After refresh, fetch fresh user info. isRetry=true: we are already inside
      // the refresh flow, so a 401 here must NOT re-enter auto-refresh (which would
      // await the still-pending refreshPromise and collapse into a spurious logout).
      api.setAccessToken(tokens.accessToken);
      const user = await api.getMe(true);
      setTokens(tokens.accessToken, tokens.refreshToken, user);
      return true;
    } catch {
      // Refresh failed — clear everything locally, no API call needed
      forceLogout();
      return false;
    }
  }, [forceLogout, setTokens]);

  // Wire up auto-refresh so the API client can silently retry 401s
  useEffect(() => {
    api.setRefreshCallback(refreshAuth);
  }, [refreshAuth]);

  /**
   * Proactive token refresh — does NOT force-logout on failure so a transient
   * network error while the user is in a background tab cannot evict their session.
   * The API client's 401 auto-refresh path remains the authoritative recovery for
   * actual request failures.
   */
  const silentRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!refreshToken) return;
    try {
      const tokens = await api.refresh(refreshToken);
      api.setAccessToken(tokens.accessToken);
      const user = await api.getMe(true);
      setTokens(tokens.accessToken, tokens.refreshToken, user);
    } catch {
      // Intentionally silent — the 401 interceptor will retry on the next API call
    }
  }, [setTokens]);

  // Proactive refresh: schedule a silent token rotation 3 min before the access
  // token expires. If the token is already past its refresh threshold (e.g. the
  // user returns to the tab after a long absence), refresh immediately.
  useEffect(() => {
    if (!state.accessToken || !state.user) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }
    const exp = parseJwtExp(state.accessToken);
    if (!exp) return;
    const msUntilRefresh = exp * 1000 - Date.now() - 3 * 60 * 1000;
    if (msUntilRefresh <= 0) {
      // Already expired or within the 3-min window — refresh now
      silentRefresh();
      return;
    }
    refreshTimerRef.current = setTimeout(silentRefresh, msUntilRefresh);
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [state.accessToken, state.user, silentRefresh]);

  // On tab re-focus / visibility change: refresh profile and check token expiration
  useEffect(() => {
    const handleSync = () => {
      if (document.visibilityState === 'visible') {
        const at = localStorage.getItem(STORAGE_KEYS.accessToken);
        if (!at) return;
        refreshUser();
        const exp = parseJwtExp(at);
        if (exp && exp * 1000 - Date.now() < 3 * 60 * 1000) {
          silentRefresh();
        }
      }
    };
    window.addEventListener('focus', handleSync);
    document.addEventListener('visibilitychange', handleSync);
    return () => {
      window.removeEventListener('focus', handleSync);
      document.removeEventListener('visibilitychange', handleSync);
    };
  }, [refreshUser, silentRefresh]);

  // Listen for storage events & BroadcastChannel messages to sync profile changes across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.user && e.newValue) {
        try {
          const newUser = JSON.parse(e.newValue) as User;
          setState((prev) => ({ ...prev, user: newUser }));
        } catch {
          // ignore
        }
      } else if (e.key === 'wp_profile_updated' || e.key === 'wp_user_updated') {
        refreshUser();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    let bc: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        bc = new BroadcastChannel('wp_auth_sync');
        bc.onmessage = (event) => {
          if (event.data?.type === 'PROFILE_UPDATED' || event.data?.type === 'USER_UPDATED') {
            refreshUser();
          }
        };
      } catch {
        // ignore
      }
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (bc) bc.close();
    };
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ ...state, setTokens, logout, clearSession, refreshAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Write an admin-panel session directly to localStorage without triggering a
 * React state change. Used during SSO login so org admins silently acquire an
 * auth-frontend session without causing the `isSso → clearSession` guard to
 * fire and immediately erase the tokens.
 */
export function persistAdminSession(accessToken: string, refreshToken: string, user: User): void {
  try {
    localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
    localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  } catch {
    // localStorage may be blocked in restrictive private-browsing contexts — fail silently
  }
}
